import React, { useState, useEffect, useMemo } from 'react';
import './DataTable.css';

const DataTable = ({
    limit,
    defaultSortKey = null,
    defaultSortDirection = 'asc',
    showPagination = false,
}) => {
    const [data, setData] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [sortConfig, setSortConfig] = useState({ key: defaultSortKey, direction: defaultSortDirection });
    const [currentPage, setCurrentPage] = useState(1);

    useEffect(() => {
        const fetchData = async () => {
            try {
                console.log('Fetching data from webhook...');

                const response = await fetch('/webhook/439406d1-fe10-453d-905d-63afc433683e', {
                    method: 'GET',
                    headers: {
                        'Content-Type': 'application/json',
                    }
                });

                console.log('Response status:', response.status);

                if (!response.ok) {
                    throw new Error(`HTTP error! status: ${response.status}`);
                }

                const result = await response.json();
                console.log('Raw data received:', result);
                console.log('Type of result:', typeof result);
                console.log('Is array?', Array.isArray(result));

                // Проверяем структуру данных
                let tableData = [];

                if (Array.isArray(result)) {
                    // Если это массив с объектами {json: {...}}
                    tableData = result.map(item => item.json || item);
                } else if (result && typeof result === 'object') {
                    // Если это объект, проверяем разные варианты
                    if (result.data && Array.isArray(result.data)) {
                        tableData = result.data.map(item => item.json || item);
                    } else if (result.result && Array.isArray(result.result)) {
                        tableData = result.result.map(item => item.json || item);
                    } else if (result.json) {
                        // Если это один объект с json
                        tableData = [result.json];
                    } else {
                        // Если это просто объект с данными
                        tableData = [result];
                    }
                }

                console.log('Processed table data:', tableData);

                const processed = tableData.map(item => {
                    const deposit = parseFloat(item.deposit) || 0;
                    const balance = parseFloat(item.balance) || 0;
                    const pnl = balance - deposit;
                    const pnlPercent = deposit ? (pnl / deposit) * 100 : 0;
                    const fee = pnl > 0 ? pnl * 0.3 : 0;
                    return { ...item, deposit, balance, pnl, pnlPercent, fee };
                });

                setData(processed);
                setLoading(false);
                setError(null);
            } catch (error) {
                console.error('Detailed error:', error);
                setError(error.message);
                setLoading(false);
            }
        };

        // Первый запрос
        fetchData();

        // Обновление каждые 30 секунд
        const interval = setInterval(fetchData, 30000);

        // Очистка интервала при размонтировании компонента
        return () => clearInterval(interval);
    }, []);

    const formatNumber = (num) => {
        if (num === null || num === undefined) return '0.00';
        return parseFloat(num).toLocaleString('en-US', {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2
        });
    };

    const formatPercent = (num) => {
        if (num === null || num === undefined) return '0.0000%';
        return `${parseFloat(num).toFixed(4)}%`;
    };

    const formatPrimeId = (id) => {
        return id || '-';
    };

    const requestSort = (key) => {
        let direction = 'asc';
        if (sortConfig.key === key && sortConfig.direction === 'asc') {
            direction = 'desc';
        }
        setSortConfig({ key, direction });
        setCurrentPage(1);
    };

    const sortedData = useMemo(() => {
        const sortable = [...data];
        if (sortConfig.key) {
            sortable.sort((a, b) => {
                const aVal = a[sortConfig.key];
                const bVal = b[sortConfig.key];
                if (aVal < bVal) return sortConfig.direction === 'asc' ? -1 : 1;
                if (aVal > bVal) return sortConfig.direction === 'asc' ? 1 : -1;
                return 0;
            });
        }
        return sortable;
    }, [data, sortConfig]);

    const pageCount = useMemo(() => {
        if (!limit) return 1;
        return Math.ceil(sortedData.length / limit);
    }, [sortedData, limit]);

    const displayedData = useMemo(() => {
        if (!limit) return sortedData;
        const start = (currentPage - 1) * limit;
        return sortedData.slice(start, start + limit);
    }, [sortedData, limit, currentPage]);

    const totals = useMemo(() => {
        const deposit = sortedData.reduce((sum, r) => sum + r.deposit, 0);
        const balance = sortedData.reduce((sum, r) => sum + r.balance, 0);
        const pnl = balance - deposit;
        const pnlPercent = deposit ? (pnl / deposit) * 100 : 0;
        const fee = sortedData.reduce((sum, r) => sum + r.fee, 0);
        return { deposit, balance, pnl, pnlPercent, fee };
    }, [sortedData]);

    if (loading) {
        return <div className="loading">Loading data...</div>;
    }

    if (error) {
        return (
            <div className="error-container">
                <div className="error-icon">⚠️</div>
                <div className="error-message">
                    <h3>Error loading data</h3>
                    <p>{error}</p>
                    <p className="error-hint">
                        Check console for details. This might be a CORS issue - configure your n8n webhook to allow cross-origin requests.
                    </p>
                </div>
            </div>
        );
    }

    if (!data.length) {
        return <div className="no-data">No data available</div>;
    }

    return (
        <div className="table-wrapper">
            <div className="table-container">
                <h2 className="table-title">Client Balances</h2>
                <table className="data-table">
                    <thead>
                    <tr>
                        <th className="sortable" onClick={() => requestSort('user')}>User</th>
                        <th className="sortable" onClick={() => requestSort('prime_id')}>PRIME ID</th>
                        <th className="sortable" onClick={() => requestSort('deposit')}>Deposit</th>
                        <th className="sortable" onClick={() => requestSort('balance')}>Balance</th>
                        <th className="sortable" onClick={() => requestSort('pnl')}>PnL</th>
                        <th className="sortable" onClick={() => requestSort('pnlPercent')}>PnL %</th>
                        <th className="sortable" onClick={() => requestSort('fee')}>Fee</th>
                    </tr>
                    </thead>
                    <tbody>
                    {displayedData.map((row, index) => (
                        <tr key={row.prime_id || row.id || index}>
                            <td>{row.user || '-'}</td>
                            <td className="prime-id">{formatPrimeId(row.prime_id || row.id)}</td>
                            <td>{formatNumber(row.deposit)}</td>
                            <td>{formatNumber(row.balance)}</td>
                            <td className={row.pnl >= 0 ? 'positive' : 'negative'}>
                                {formatNumber(row.pnl)}
                            </td>
                            <td className={row.pnlPercent >= 0 ? 'positive' : 'negative'}>
                                {formatPercent(row.pnlPercent)}
                            </td>
                            <td>{formatNumber(row.fee)}</td>
                        </tr>
                    ))}
                    </tbody>
                    <tfoot>
                        <tr className="total-row">
                            <td colSpan="2">Total</td>
                            <td>{formatNumber(totals.deposit)}</td>
                            <td>{formatNumber(totals.balance)}</td>
                            <td className={totals.pnl >= 0 ? 'positive' : 'negative'}>
                                {formatNumber(totals.pnl)}
                            </td>
                            <td className={totals.pnlPercent >= 0 ? 'positive' : 'negative'}>
                                {formatPercent(totals.pnlPercent)}
                            </td>
                            <td>{formatNumber(totals.fee)}</td>
                        </tr>
                    </tfoot>
                </table>
            </div>

            <div className="table-footer">
                <span className="update-info">Auto-refresh every 30 seconds</span>
            </div>
            {showPagination && limit && pageCount > 1 && (
                <div className="pagination">
                    <button onClick={() => setCurrentPage(p => Math.max(1, p - 1))} disabled={currentPage === 1}>&lt;</button>
                    <span className="page-num">{currentPage}</span>
                    <button onClick={() => setCurrentPage(p => Math.min(pageCount, p + 1))} disabled={currentPage === pageCount}>&gt;</button>
                </div>
            )}
        </div>
    );
};

export default DataTable;